/**
 * FHIR Medication Hooks - Custom React Hooks
 * Provides easy-to-use React hooks for FHIR medication management
 */

import { medicationDisplayHelpers } from "@/services/api/fhirMedicationService";
import { useFHIRMedicationStore } from "@/stores/fhirMedicationStore";
import type {
  MedicationCreateRequest,
  MedicationQueryParams,
} from "@/types/fhir";
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Hook to fetch and manage all medications
 * @param params - Query parameters for filtering medications
 * @param autoFetch - Auto-fetch on mount (default: true)
 */
export const useFHIRMedications = (
  params?: MedicationQueryParams,
  autoFetch = true,
) => {
  const { medications, loading, error, fetchAllMedications, clearError } =
    useFHIRMedicationStore();

  useEffect(() => {
    if (autoFetch) {
      fetchAllMedications(params);
    }
  }, [autoFetch, params, fetchAllMedications]);

  const refetch = useCallback(() => {
    return fetchAllMedications(params);
  }, [params, fetchAllMedications]);

  return {
    medications,
    loading,
    error,
    refetch,
    clearError,
  };
};

/**
 * Hook to fetch and manage active medications only
 * @param autoFetch - Auto-fetch on mount (default: true)
 */
export const useActiveMedications = (autoFetch = true) => {
  const {
    activeMedications,
    loading,
    error,
    fetchActiveMedications,
    clearError,
  } = useFHIRMedicationStore();

  useEffect(() => {
    if (autoFetch) {
      fetchActiveMedications();
    }
  }, [autoFetch, fetchActiveMedications]);

  const refetch = useCallback(() => {
    return fetchActiveMedications();
  }, [fetchActiveMedications]);

  return {
    activeMedications,
    loading,
    error,
    refetch,
    clearError,
  };
};

/**
 * Hook to fetch single medication by ID
 * @param medicationId - FHIR MedicationStatement ID
 * @param autoFetch - Auto-fetch on mount (default: true)
 */
export const useFHIRMedication = (
  medicationId: string | null,
  autoFetch = true,
) => {
  const {
    selectedMedication,
    loading,
    error,
    fetchMedicationById,
    setSelectedMedication,
    clearError,
  } = useFHIRMedicationStore();

  useEffect(() => {
    if (autoFetch && medicationId) {
      fetchMedicationById(medicationId);
    }

    // Cleanup on unmount
    return () => {
      setSelectedMedication(null);
    };
  }, [autoFetch, medicationId, fetchMedicationById, setSelectedMedication]);

  const refetch = useCallback(() => {
    if (medicationId) {
      return fetchMedicationById(medicationId);
    }
    return Promise.resolve(null);
  }, [medicationId, fetchMedicationById]);

  const displayData = selectedMedication
    ? medicationDisplayHelpers.toDisplayFormat(selectedMedication)
    : null;

  return {
    medication: selectedMedication,
    displayData,
    loading,
    error,
    refetch,
    clearError,
  };
};

/**
 * Hook for medication CRUD operations
 */
export const useFHIRMedicationActions = () => {
  const {
    createMedication,
    updateMedication,
    deleteMedication,
    loading,
    error,
    clearError,
  } = useFHIRMedicationStore();

  return {
    createMedication,
    updateMedication,
    deleteMedication,
    loading,
    error,
    clearError,
  };
};

/**
 * Hook to calculate medication statistics from loaded medications
 * NOTE: Computes statistics locally from medications store to avoid separate API calls
 * @param _autoFetch - Deprecated parameter (kept for compatibility but ignored)
 */
export const useFHIRMedicationStats = (_autoFetch = true) => {
  const { medications, loading, error, clearError } = useFHIRMedicationStore();

  // Calculate statistics locally from loaded medications
  const statistics = useMemo(() => {
    if (!medications || medications.length === 0) {
      return {
        total: 0,
        active: 0,
        expiringSoon: 0,
        expired: 0,
      };
    }

    const now = new Date();
    const thirtyDaysFromNow = new Date(
      now.getTime() + 30 * 24 * 60 * 60 * 1000,
    );

    return {
      total: medications.length,
      active: medications.filter((med) => med.status === "active").length,
      expiringSoon: medications.filter((med) => {
        if (!med.endDate) return false;
        const endDate = new Date(med.endDate);
        return endDate > now && endDate <= thirtyDaysFromNow;
      }).length,
      expired: medications.filter((med) => {
        if (!med.endDate) return false;
        return new Date(med.endDate) < now;
      }).length,
    };
  }, [medications]);

  const refetch = useCallback(() => {
    // No-op: statistics are calculated from medications
    return Promise.resolve();
  }, []);

  return {
    statistics,
    loading,
    error,
    refetch,
    clearError,
  };
};

/**
 * Hook to fetch FHIR terminologies (categories, routes, frequencies)
 * @param autoFetch - Auto-fetch on mount (default: true)
 */
export const useFHIRMedicationTerminologies = (autoFetch = true) => {
  const {
    categories,
    routes,
    frequencies,
    loading,
    error,
    fetchTerminologies,
    clearError,
  } = useFHIRMedicationStore();

  useEffect(() => {
    if (autoFetch) {
      fetchTerminologies();
    }
  }, [autoFetch, fetchTerminologies]);

  const refetch = useCallback(() => {
    return fetchTerminologies();
  }, [fetchTerminologies]);

  return {
    categories,
    routes,
    frequencies,
    loading,
    error,
    refetch,
    clearError,
  };
};

/**
 * Hook to filter medications by category
 * @param category - SNOMED CT category code
 * @param autoFetch - Auto-fetch on mount (default: true)
 */
export const useFHIRMedicationsByCategory = (
  category: string | null,
  autoFetch = true,
) => {
  const {
    medications,
    loading,
    error,
    fetchMedicationsByCategory,
    clearError,
  } = useFHIRMedicationStore();

  useEffect(() => {
    if (autoFetch && category) {
      fetchMedicationsByCategory(category);
    }
  }, [autoFetch, category, fetchMedicationsByCategory]);

  const refetch = useCallback(() => {
    if (category) {
      return fetchMedicationsByCategory(category);
    }
    return Promise.resolve();
  }, [category, fetchMedicationsByCategory]);

  return {
    medications,
    loading,
    error,
    refetch,
    clearError,
  };
};

/**
 * Hook to filter medications expiring soon from already loaded medications
 * NOTE: This hook filters locally from existing medications store.
 * It does NOT fetch from API to avoid overwriting the medications list.
 * @param _autoFetch - Deprecated parameter (kept for compatibility but ignored)
 */
export const useExpiringMedications = (_autoFetch = true) => {
  const { medications, loading, error, clearError } = useFHIRMedicationStore();

  // NOTE: Removed fetchExpiringMedications() call because it overwrites the medications list
  // Instead, we filter the already-loaded medications locally

  const refetch = useCallback(() => {
    // No-op: medications are already loaded by useFHIRMedications()
    return Promise.resolve();
  }, []);

  // Filter expired vs expiring soon (with null safety)
  const expiringSoon = (medications || []).filter(
    (med) => med.raw && medicationDisplayHelpers.isExpiringSoon(med.raw),
  );

  const expired = (medications || []).filter(
    (med) => med.raw && medicationDisplayHelpers.isExpired(med.raw),
  );

  return {
    expiringMedications: expiringSoon,
    expiredMedications: expired,
    allMedications: medications || [],
    loading,
    error,
    refetch,
    clearError,
  };
};

/**
 * Hook for complete medication management (all-in-one)
 * Provides all medication data and actions
 */
export const useFHIRMedicationManagement = () => {
  const store = useFHIRMedicationStore();

  const displayMedications = store.medications;
  const displayActiveMedications = store.activeMedications;

  return {
    // Data
    medications: displayMedications,
    activeMedications: displayActiveMedications,
    selectedMedication: store.selectedMedication,
    statistics: store.statistics,
    categories: store.categories,
    routes: store.routes,
    frequencies: store.frequencies,

    // State
    loading: store.loading,
    error: store.error,

    // Fetch Actions
    fetchAllMedications: store.fetchAllMedications,
    fetchActiveMedications: store.fetchActiveMedications,
    fetchMedicationById: store.fetchMedicationById,
    fetchMedicationsByCategory: store.fetchMedicationsByCategory,
    fetchExpiringMedications: store.fetchExpiringMedications,
    fetchStatistics: store.fetchStatistics,
    fetchTerminologies: store.fetchTerminologies,

    // CRUD Actions
    createMedication: store.createMedication,
    updateMedication: store.updateMedication,
    deleteMedication: store.deleteMedication,

    // Utility Actions
    setSelectedMedication: store.setSelectedMedication,
    clearError: store.clearError,
    refreshData: store.refreshData,
  };
};

/**
 * Hook for medication form creation
 * Provides form state and helpers for creating new medications
 */
export const useFHIRMedicationForm = () => {
  const { createMedication, updateMedication, loading, error, clearError } =
    useFHIRMedicationActions();
  const { categories, routes, frequencies } = useFHIRMedicationTerminologies();

  const [formData, setFormData] = useState<Partial<MedicationCreateRequest>>({
    name: "",
    category: "",
    dosageText: "",
    routeCode: "",
    frequencyCode: "",
    startDate: "",
    endDate: "",
    prescriber: "",
    pharmacy: "",
    instructions: "",
    refills: 0,
  });

  const updateFormField = useCallback(
    (
      field: keyof MedicationCreateRequest,
      value: string | number | boolean,
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      category: "",
      dosageText: "",
      routeCode: "",
      frequencyCode: "",
      startDate: "",
      endDate: "",
      prescriber: "",
      pharmacy: "",
      instructions: "",
      refills: 0,
    });
    clearError();
  }, [clearError]);

  const handleSubmit = useCallback(
    async (medicationId?: string) => {
      if (medicationId) {
        return await updateMedication(medicationId, formData as any);
      } else {
        return await createMedication(formData as any);
      }
    },
    [formData, createMedication, updateMedication],
  );

  return {
    formData,
    updateFormField,
    resetForm,
    handleSubmit,
    loading,
    error,
    clearError,
    // Terminologies for dropdowns
    categories,
    routes,
    frequencies,
  };
};

export default {
  useFHIRMedications,
  useActiveMedications,
  useFHIRMedication,
  useFHIRMedicationActions,
  useFHIRMedicationStats,
  useFHIRMedicationTerminologies,
  useFHIRMedicationsByCategory,
  useExpiringMedications,
  useFHIRMedicationManagement,
  useFHIRMedicationForm,
};
